import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { FileText, ArrowRight, Volume2, Play, Sparkles, Check, X, Lock, Globe2, EyeOff, Wifi, WifiLow, Search, ZoomIn, Maximize, LayoutGrid, Keyboard, Hand, MousePointerClick, Type, Info, Link2, ImageIcon } from "lucide-react";
import { LayerVisual } from "@/components/layers";

const useCycle = (n, ms) => {
  const [i, setI] = useState(0);
  useEffect(() => { const t = setInterval(() => setI((x) => (x + 1) % n), ms); return () => clearInterval(t); }, [n, ms]);
  return i;
};

export const Frame = ({ children, className = "" }) => (
  <div className={`neu-panel relative overflow-hidden rounded-[28px] p-6 ${className}`}>{children}</div>
);

const Chip = ({ children, active, color = "#3D3DF0" }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all ${active ? "text-white" : "neu-flat text-[#334155]"}`} style={active ? { background: color } : {}}>{children}</span>
);

const MiniPage = ({ className = "", children, lines = 5 }) => (
  <div className={`relative rounded-lg bg-white p-3 shadow-[0_10px_24px_-12px_rgba(15,23,42,.35)] ${className}`}>
    <div className="mb-2 h-2.5 w-2/3 rounded bg-[#3D3DF0]/70" />
    {Array.from({ length: lines }).map((_, i) => <div key={i} className="mb-1.5 h-1.5 rounded bg-slate-200" style={{ width: `${90 - (i % 3) * 15}%` }} />)}
    {children}
  </div>
);

export const ImportIllo = () => (
  <Frame className="flex h-80 items-center justify-between gap-4">
    <div className="il-float flex flex-col items-center gap-2">
      <div className="grid h-28 w-24 place-items-center rounded-xl bg-white shadow-lg"><FileText className="h-10 w-10 text-[#A62B8A]" /></div>
      <span className="font-mono text-[11px] text-[#64748B]">textbook.pdf</span>
    </div>
    <div className="flex flex-col items-center gap-1 text-[#A62B8A]">
      <span className="font-mono text-[10px] uppercase tracking-widest">Ingestion</span>
      <div className="flex gap-1">{[0, 1, 2].map((i) => <span key={i} className="h-2 w-2 animate-pulse rounded-full bg-[#A62B8A]" style={{ animationDelay: `${i * 200}ms` }} />)}</div>
      <ArrowRight className="h-5 w-5" />
    </div>
    <div className="relative flex flex-col gap-2">
      {[["1600px", "Desktop / HD"], ["800px", "Mobile"], ["240px", "Thumbnail"]].map(([w, l], i) => (
        <div key={w} className="il-pop flex items-center gap-2 rounded-xl bg-white px-3 py-2 shadow" style={{ animationDelay: `${i * 300}ms` }}>
          <span className="h-6 w-5 rounded-sm bg-gradient-to-br from-[#26C4D5] to-[#3D3DF0]" />
          <span className="font-mono text-[11px] font-medium">{w}.webp</span>
          <span className="text-[10px] text-[#64748B]">{l}</span>
        </div>
      ))}
      <div className="relative mt-1 overflow-hidden rounded-xl bg-white px-3 py-2 shadow">
        <span className="il-scan absolute inset-x-0 h-0.5 bg-[#5FA82E]" />
        <span className="font-notebook text-xs text-[#0F172A]">“Photosynthesis converts light…”</span>
        <p className="font-mono text-[10px] text-[#5FA82E]">searchable text layer</p>
      </div>
    </div>
  </Frame>
);

export const EditorIllo = () => (
  <Frame className="h-80 p-4">
    <div className="flex h-full gap-3">
      <div className="neu-inset flex w-14 flex-col gap-2 rounded-2xl p-2">
        {[0, 1, 2].map((i) => <div key={i} className={`h-14 rounded-md bg-white ${i === 0 ? "ring-2 ring-[#3D3DF0]" : ""}`} />)}
      </div>
      <div className="relative flex-1">
        <MiniPage className="h-full" lines={7}>
          <div className="il-drag absolute left-8 top-16 h-9 w-9 outline outline-2 outline-offset-2 outline-[#3D3DF0]">
            <LayerVisual layer={{ type: "hotspot", style: "pulse", action: "video" }} />
            <span className="absolute -top-6 left-0 rounded bg-[#3D3DF0] px-1.5 font-mono text-[9px] text-white">Hotspot</span>
          </div>
          <div className="absolute bottom-4 right-4 h-14 w-24 overflow-hidden rounded bg-slate-900"><div className="grid h-full place-items-center"><Play className="h-4 w-4 text-white" fill="white" /></div></div>
        </MiniPage>
        <div className="neu-raised absolute -bottom-1 left-1/2 flex -translate-x-1/2 gap-1 rounded-full p-1.5">
          {[Volume2, Play, ImageIcon, MousePointerClick, Type, Info, Link2].map((I, i) => <span key={i} className={`grid h-7 w-7 place-items-center rounded-full ${i === 3 ? "bg-[#3D3DF0] text-white" : "text-[#334155]"}`}><I className="h-3.5 w-3.5" /></span>)}
        </div>
      </div>
      <div className="neu-raised hidden w-28 flex-col gap-2 rounded-2xl p-3 sm:flex">
        <span className="font-mono text-[9px] uppercase tracking-widest text-[#3D3DF0]">Properties</span>
        {["Style · Pulse", "On click · Video", "Autoplay", "Fade in"].map((t) => <span key={t} className="neu-inset rounded-lg px-2 py-1.5 text-[9px]">{t}</span>)}
      </div>
    </div>
  </Frame>
);

export const HotspotIllo = () => (
  <Frame className="grid h-80 grid-cols-3 gap-4">
    {["pulse", "glow", "circle", "arrow", "icon", "invisible"].map((s) => (
      <div key={s} className="neu-flat flex flex-col items-center justify-center gap-3 rounded-2xl bg-white/40">
        <div className={`h-12 w-12 ${s === "invisible" ? "rounded border-2 border-dashed border-[#A62B8A]/60" : ""}`}><LayerVisual layer={{ type: "hotspot", style: s, action: s === "icon" ? "audio" : "popup" }} /></div>
        <span className="font-mono text-[10px] uppercase tracking-widest text-[#64748B]">{s}</span>
      </div>
    ))}
  </Frame>
);

export const AudioIllo = () => {
  const words = "The little lion walked into the forest.".split(" ");
  const w = useCycle(words.length + 2, 450);
  return (
    <Frame className="flex h-80 flex-col justify-center gap-8">
      <MiniPage lines={3}>
        <p className="mt-2 text-sm leading-relaxed">
          {words.map((x, i) => <span key={i} className={`rounded px-0.5 transition-colors ${i === w ? "bg-[#F7933E]/40" : i < w ? "text-[#3D3DF0]" : ""}`}>{x} </span>)}
        </p>
        <span className="absolute -right-3 -top-3 grid h-10 w-10 place-items-center rounded-full bg-[#3D3DF0] text-white shadow-lg ring-4 ring-[#3D3DF0]/20"><Volume2 className="h-4 w-4" /></span>
      </MiniPage>
      <div className="flex items-end gap-6">
        <div className="il-wave flex h-14 items-end gap-1">
          {Array.from({ length: 18 }).map((_, i) => <span key={i} className="w-1.5 rounded-full sg-gradient" style={{ height: `${30 + ((i * 37) % 70)}%`, animationDelay: `${i * 70}ms` }} />)}
        </div>
        <div className="flex flex-wrap gap-2">{["Click to play", "On page open", "Loop", "Stop on leave"].map((c, i) => <Chip key={c} active={i === 1}>{c}</Chip>)}</div>
      </div>
    </Frame>
  );
};

export const VideoIllo = () => {
  const stage = useCycle(3, 1600);
  return (
    <Frame className="flex h-80 flex-col gap-4">
      <div className="relative flex-1 overflow-hidden rounded-2xl bg-gradient-to-br from-[#241B4A] via-[#3D3DF0] to-[#A62B8A]">
        <div className={`absolute inset-0 grid place-items-center transition-opacity duration-500 ${stage === 2 ? "opacity-0" : "opacity-100"}`}>
          <span className={`grid h-16 w-16 place-items-center rounded-full bg-white text-[#3D3DF0] shadow-2xl transition-transform ${stage === 1 ? "scale-90" : "scale-100"}`}><Play className="ml-1 h-6 w-6" fill="currentColor" /></span>
        </div>
        {stage === 2 && <div className="absolute inset-x-4 bottom-4 h-1.5 overflow-hidden rounded-full bg-white/30"><div className="il-fill h-full rounded-full bg-[#26C4D5]" style={{ "--w": "70%" }} /></div>}
        <span className="absolute left-4 top-4 rounded-full bg-black/30 px-3 py-1 font-mono text-[10px] text-white">{["POSTER ONLY · 0 KB VIDEO", "CLICK → LOAD", "STREAMING"][stage]}</span>
      </div>
      <div className="flex flex-wrap gap-2">{["Inline", "Popup modal", "YouTube / Vimeo", "Muted autoplay", "Start / end time"].map((c, i) => <Chip key={c} active={i === stage}>{c}</Chip>)}</div>
    </Frame>
  );
};

export const AiIllo = () => (
  <Frame className="flex h-80 flex-col gap-3">
    <div className="neu-btn-primary flex w-fit items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold"><Sparkles className="h-3.5 w-3.5" />Analyze this book</div>
    {[["Page 4", "Audio narration", 87, "#F7933E"], ["Page 8", "Video hotspot", 92, "#A62B8A"], ["Page 12", "Knowledge check", 82, "#5FA82E"]].map(([p, t, c, col], i) => (
      <div key={p} className="il-pop flex items-center gap-3 rounded-2xl bg-white p-3 shadow" style={{ animationDelay: `${i * 450}ms` }}>
        <span className="font-mono text-[10px] text-[#64748B]">{p}</span>
        <div className="flex-1">
          <p className="text-xs font-semibold">{t}</p>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="il-fill h-full rounded-full" style={{ "--w": `${c}%`, background: col }} /></div>
        </div>
        <span className="font-mono text-xs font-medium" style={{ color: col }}>{c}%</span>
        <span className="grid h-6 w-6 place-items-center rounded-full bg-[#5FA82E]/15 text-[#5FA82E]"><Check className="h-3 w-3" /></span>
        <span className="grid h-6 w-6 place-items-center rounded-full bg-slate-100 text-[#64748B]"><X className="h-3 w-3" /></span>
      </div>
    ))}
  </Frame>
);

export const FlipIllo = ({ big }) => (
  <div className={`relative mx-auto ${big ? "h-[360px] w-full max-w-[560px]" : "h-60 w-full max-w-[420px]"}`} style={{ perspective: 1600 }}>
    <div className="absolute inset-0 flex">
      <div className="h-full w-1/2 rounded-l-xl bg-white p-5 shadow-[-10px_20px_40px_-20px_rgba(15,23,42,.45)]">
        <div className="mb-3 h-3 w-1/2 rounded bg-[#A62B8A]/70" />
        {Array.from({ length: big ? 9 : 6 }).map((_, i) => <div key={i} className="mb-2 h-1.5 rounded bg-slate-200" style={{ width: `${92 - (i % 4) * 12}%` }} />)}
        <div className="mt-4 h-16 rounded-lg bg-gradient-to-br from-[#26C4D5]/40 to-[#84D43E]/40" />
      </div>
      <div className="h-full w-1/2 rounded-r-xl bg-white p-5 shadow-[10px_20px_40px_-20px_rgba(15,23,42,.45)]">
        <div className="mb-3 h-3 w-2/3 rounded bg-[#3D3DF0]/70" />
        {Array.from({ length: big ? 9 : 6 }).map((_, i) => <div key={i} className="mb-2 h-1.5 rounded bg-slate-200" style={{ width: `${88 - (i % 3) * 14}%` }} />)}
        <div className="relative mt-4 h-9 w-9"><LayerVisual layer={{ type: "hotspot", style: "pulse", action: "video" }} /></div>
      </div>
    </div>
    <div className="pointer-events-none absolute inset-y-0 left-1/2 w-1/2 il-flip">
      <div className="absolute inset-0 rounded-r-xl bg-gradient-to-r from-slate-100 to-white p-5" style={{ backfaceVisibility: "hidden" }}>
        <div className="mb-3 h-3 w-1/2 rounded bg-[#A62B8A]/70" />
        {Array.from({ length: big ? 8 : 5 }).map((_, i) => <div key={i} className="mb-2 h-1.5 rounded bg-slate-200" style={{ width: `${85 - (i % 3) * 10}%` }} />)}
      </div>
      <div className="absolute inset-0 rounded-l-xl bg-gradient-to-l from-slate-100 to-white p-5" style={{ transform: "rotateY(180deg)", backfaceVisibility: "hidden" }}>
        <div className="mb-3 h-3 w-2/5 rounded bg-[#F7933E]/70" />
        {Array.from({ length: big ? 8 : 5 }).map((_, i) => <div key={i} className="mb-2 h-1.5 rounded bg-slate-200" style={{ width: `${90 - (i % 3) * 12}%` }} />)}
        <div className="mt-4 h-14 rounded-lg bg-gradient-to-br from-[#3D3DF0]/25 to-[#A62B8A]/25" />
      </div>
    </div>
    <div className="pointer-events-none absolute inset-y-0 left-1/2 w-6 -translate-x-1/2 bg-gradient-to-r from-transparent via-slate-900/15 to-transparent" />
  </div>
);

export const PreloadIllo = () => {
  const cur = useCycle(7, 1100) + 3;
  const state = (i) => { const d = Math.abs(i - cur); return d === 0 ? "current" : d === 1 ? "preloaded" : d === 2 ? "next" : "idle"; };
  const styles = { current: "bg-[#3D3DF0] text-white scale-110", preloaded: "bg-[#A62B8A]/70 text-white", next: "bg-[#A62B8A]/30 text-[#0F172A]", idle: "border-2 border-dashed border-slate-300 text-slate-400" };
  return (
    <Frame className="flex h-80 flex-col justify-center gap-6">
      <div className="flex justify-center gap-2">
        {Array.from({ length: 13 }).map((_, i) => <div key={i} className={`grid h-16 w-10 place-items-center rounded-md font-mono text-[10px] transition-all duration-300 ${styles[state(i)]}`}>{i + 1}</div>)}
      </div>
      <div className="flex flex-wrap justify-center gap-3 text-[11px]">
        {[["#3D3DF0", "On screen"], ["#A62B8A", "Preloaded ±1"], ["#A62B8A", "Next priority ±2"], ["#cbd5e1", "Not loaded"]].map(([c, l]) => <span key={l} className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm" style={{ background: c }} />{l}</span>)}
      </div>
      <div className="flex justify-center gap-8 font-mono text-xs">
        <span>First page <b className="text-[#3D3DF0]">0.9s</b></span><span>Turn <b className="text-[#A62B8A]">82ms</b></span><span>Hotspot <b className="text-[#A62B8A]">46ms</b></span>
      </div>
    </Frame>
  );
};

export const ToolsIllo = () => {
  const tools = [[LayoutGrid, "Thumbnails"], [Search, "Search text"], [ZoomIn, "Zoom 300%"], [Maximize, "Fullscreen"], [Hand, "Swipe"], [Keyboard, "Keyboard ← →"]];
  const a = useCycle(tools.length, 1200);
  return (
    <Frame className="flex h-80 flex-col justify-center gap-6">
      <div className="neu-inset mx-auto flex w-full max-w-sm items-center gap-2 rounded-full px-4 py-3">
        <Search className="h-4 w-4 text-[#3D3DF0]" /><span className="font-notebook text-sm">photosynthesis</span>
        <span className="ml-auto rounded-full bg-[#F7933E]/20 px-2 font-mono text-[10px] text-[#c2620f]">Page 2, 3</span>
      </div>
      <div className="neu-raised mx-auto flex gap-1 rounded-full p-2">
        {tools.map(([I, l], i) => <span key={l} title={l} className={`grid h-10 w-10 place-items-center rounded-full transition-all ${i === a ? "neu-pressed text-[#3D3DF0]" : "text-[#334155]"}`}><I className="h-4 w-4" /></span>)}
      </div>
      <p className="text-center font-display text-lg font-bold">{tools[a][1]}</p>
    </Frame>
  );
};

export const PublishIllo = () => {
  const tabs = ["Direct URL", "iframe", "Wix element", "QR code"];
  const t = useCycle(4, 1800);
  const code = ["books.ibookstudio.com/read/abc123", '<iframe src=".../embed/abc123" allowfullscreen></iframe>', '<ibookstudio-book book="abc123"></ibookstudio-book>'];
  return (
    <Frame className="flex h-80 flex-col gap-4">
      <div className="flex flex-wrap gap-2">{tabs.map((x, i) => <Chip key={x} active={i === t} color={["#3D3DF0", "#A62B8A", "#A62B8A", "#F7933E"][i]}>{x}</Chip>)}</div>
      <div className="neu-inset grid flex-1 place-items-center rounded-2xl p-4">
        {t < 3 ? <code className="break-all text-center font-mono text-xs text-[#334155]">{code[t]}</code> : <div className="rounded-xl bg-white p-2"><QRCodeSVG value="https://ibookstudio.com" size={96} fgColor="#241B4A" /></div>}
      </div>
      <div className="flex items-center gap-2 font-mono text-[11px]">
        {["Draft", "v1.0", "v1.1"].map((v, i) => <span key={v} className="flex items-center gap-2"><span className={`rounded-full px-2.5 py-1 ${i === 2 ? "bg-[#5FA82E] text-white" : "neu-flat"}`}>{v}{i === 2 && " · LIVE"}</span>{i < 2 && <ArrowRight className="h-3 w-3 text-[#64748B]" />}</span>)}
      </div>
    </Frame>
  );
};

export const AccessIllo = () => {
  const opts = [[Globe2, "Public", "#5FA82E"], [EyeOff, "Unlisted", "#A62B8A"], [Lock, "Password", "#A62B8A"]];
  const a = useCycle(3, 1500);
  const slow = useCycle(2, 2200);
  return (
    <Frame className="grid h-80 grid-cols-2 gap-4">
      <div className="flex flex-col justify-center gap-3">
        {opts.map(([I, l, c], i) => (
          <div key={l} className={`flex items-center gap-3 rounded-2xl p-3 transition-all ${i === a ? "neu-pressed" : "neu-flat"}`}>
            <span className="grid h-8 w-8 place-items-center rounded-full text-white" style={{ background: c }}><I className="h-4 w-4" /></span>
            <span className="text-sm font-semibold">{l}</span>
            {i === 2 && i === a && <span className="ml-auto font-mono text-xs tracking-widest">••••</span>}
          </div>
        ))}
      </div>
      <div className="neu-inset flex flex-col items-center justify-center gap-3 rounded-2xl p-4 text-center">
        {slow ? <WifiLow className="h-8 w-8 text-[#F7933E]" /> : <Wifi className="h-8 w-8 text-[#5FA82E]" />}
        <p className="font-display text-base font-bold">{slow ? "Low-bandwidth mode" : "Full quality"}</p>
        <p className="text-[11px] text-[#64748B]">{slow ? "800px pages · smaller preload window · click-to-load media" : "1600px pages · ±2 page preload"}</p>
      </div>
    </Frame>
  );
};

export const QuizIllo = () => {
  const step = useCycle(4, 1500);
  const opts = ["Evaporation", "Condensation", "Collection"];
  return (
    <Frame className="flex h-80 flex-col gap-4">
      <div className="flex gap-1.5">{[0, 1, 2, 3].map((k) => <span key={k} className={`h-1.5 flex-1 rounded-full ${k <= step ? "bg-[#3D3DF0]" : "bg-slate-200"}`} />)}</div>
      {step < 3 ? (
        <>
          <p className="font-display text-lg font-bold">Which step turns water vapour into clouds?</p>
          <div className="grid gap-2">
            {opts.map((o, j) => (
              <span key={o} className={`flex items-center justify-between rounded-2xl px-4 py-2.5 text-sm font-semibold transition-all ${step >= 1 && j === 1 ? "bg-[#5FA82E] text-white" : step >= 1 && j === 0 ? "bg-[#A62B8A] text-white" : "neu-flat"}`}>
                {o}{step >= 1 && j === 1 && <Check className="h-4 w-4" />}{step >= 1 && j === 0 && <X className="h-4 w-4" />}
              </span>
            ))}
          </div>
          {step >= 2 && <p className="il-pop text-xs font-semibold text-[#5FA82E]" style={{ animationIterationCount: 1 }}>Correct answer: Condensation — cool air turns vapour into droplets.</p>}
        </>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <p className="font-display text-5xl font-extrabold sg-text-gradient">75%</p>
          <p className="mt-1 text-sm text-[#64748B]">3 of 4 correct · 30/40 points</p>
          <p className="mt-2 font-display font-bold text-[#5FA82E]">Passed — great work!</p>
          <span className="neu-btn-primary mt-4 rounded-full px-5 py-2 text-xs font-bold">Retry quiz</span>
        </div>
      )}
    </Frame>
  );
};

export const AnalyticsIllo = () => {
  const t = useCycle(3, 1600);
  const bars = [[40, 62, 55, 80, 70, 92, 60, 75], [30, 45, 70, 50, 85, 65, 90, 58], [55, 38, 66, 72, 48, 88, 77, 95]][t];
  return (
    <Frame className="flex h-80 flex-col gap-4">
      <div className="grid grid-cols-3 gap-3">
        {[["Views", ["2,431", "2,512", "2,604"][t], "#3D3DF0"], ["Pages read", ["13.4", "13.9", "14.2"][t], "#A62B8A"], ["Hotspot taps", ["1,206", "1,288", "1,341"][t], "#A62B8A"]].map(([l, v, c]) => (
          <div key={l} className="rounded-2xl bg-white p-3 shadow"><p className="text-[9px] font-bold uppercase tracking-widest text-[#64748B]">{l}</p><p className="font-display text-xl font-extrabold" style={{ color: c }}>{v}</p></div>
        ))}
      </div>
      <div className="neu-inset flex flex-1 items-end gap-2 rounded-2xl p-4">
        {bars.map((h, i) => <div key={i} className="flex-1 rounded-t-lg sg-gradient transition-all duration-700" style={{ height: `${h}%` }} />)}
      </div>
      <div className="flex justify-between font-mono text-[11px]"><span>Quiz pass rate <b className="text-[#5FA82E]">{[74, 76, 79][t]}%</b></span><span>Completion <b className="text-[#3D3DF0]">{[68, 70, 71][t]}%</b></span></div>
    </Frame>
  );
};
